/**
 * Level 3 — the Settings sheet.
 *
 * ============================== WHAT IS IN HERE ================================
 * Things about the PLAYER, not about the game. `PRODUCT-FLOW-AND-PRICING.md` §2's own
 * test: if it does not change the hand, it is Level 3. Money display, pace, auto-deal,
 * sounds, taunts, deck colour, reduced motion — and an Advanced group holding the four
 * dials that decide how a table is DEALT rather than what is dealt (hero seat, fast
 * bots, seed, seed lock), which is where the setup form's "More options" went.
 *
 * A bottom sheet on a phone and a side panel on a desktop, from one markup and one
 * media query — the same discipline the table follows. It is reachable from the gear on
 * every room screen and from the ⋯ on the table, and from nowhere on the way to a hand.
 *
 * ## Two settings that are not stored here
 *
 * Money display and pace already have stores (`lib/money.ts`, `table/settings.ts`) and
 * this reads and writes through them, because the alternative is two copies of one
 * setting. Everything else is `settings/prefs.ts`.
 * ===============================================================================
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { MONEY_MODES, loadMoneyMode, saveMoneyMode, onMoneyModeChange, type MoneyMode } from "../lib/money";
import { getPace, setPace, onPaceChange, PACE_OPTIONS, prefersReducedMotion } from "../table/settings";
import type { Pace } from "../table/pace";
import { getPrefs, setPref, usePrefsStore, type DeckColour, type TableView } from "./prefs";
import { useTableView } from "./view";
import { setMuted, setVolume, unlock } from "../table/sounds";
import "../rooms/rooms.css";

export function SettingsSheet({ onClose }: { onClose: () => void }) {
  const { prefs } = usePrefsStore();
  const [money, setMoney] = useState<MoneyMode>(loadMoneyMode);
  const [pace, setPaceState] = useState<Pace>(getPace);
  useEffect(() => onMoneyModeChange(() => setMoney(loadMoneyMode())), []);
  useEffect(() => onPaceChange(() => setPaceState(getPace())), []);

  /* Escape closes it. A sheet that can only be closed by finding a small × is a trap. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const osReduced = prefersReducedMotion();
  /* Phase 7: the table's projection. Offered only where the saloon fits. */
  const table = useTableView();

  /*
   * PORTALLED TO THE BODY, and it is not tidiness.
   *
   * The rooms are laid out by `@container`, and `container-type: inline-size` brings
   * layout containment with it — which makes the container a containing block for
   * FIXED descendants. A sheet rendered inside a room would therefore be positioned
   * against the room rather than the viewport, and opening it after scrolling down the
   * Play tab would put it off the top of the screen. Out here it is fixed to the window,
   * which is what a sheet is.
   */
  return createPortal((
    <div className="sheet-scrim" onClick={onClose}>
      <aside className="sheet" role="dialog" aria-label="Settings"
             onClick={(e) => e.stopPropagation()}>
        <header className="sheet-head">
          <h2>Settings</h2>
          <button className="sheet-close" type="button" onClick={onClose} aria-label="Close settings">×</button>
        </header>

        <div className="sheet-body">
          <Group name="Money">
            <Seg label="Show amounts as"
                 options={MONEY_MODES.map((m) => ({ id: m.id, label: m.label, hint: m.hint }))}
                 value={money}
                 onPick={(id) => { saveMoneyMode(id as MoneyMode); setMoney(id as MoneyMode); }} />
          </Group>

          <Group name="The table">
            {/*
              * PHASE 7: Saloon / Top-down. The same preference as the header's switch.
              * On a screen the saloon does not fit, it says why instead of offering a
              * choice that would do nothing.
              */}
            {table.fits ? (
              <Seg label="View"
                   options={[
                     { id: "saloon", label: "Saloon", hint: "The table from your chair, the cast standing round it." },
                     { id: "topdown", label: "Top-down", hint: "The whole table from above — quicker to read." },
                   ]}
                   value={table.view}
                   onPick={(id) => table.set(id as TableView)} />
            ) : (
              <p className="sheet-line muted small">
                View: top-down. The saloon view needs a wide screen — a laptop or a desktop.
              </p>
            )}
            <Seg label="Pace"
                 options={PACE_OPTIONS.map((p) => ({ id: p.id, label: p.label }))}
                 value={pace}
                 onPick={(id) => setPace(id as Pace)} />
            <Check label="Deal the next hand automatically"
                   checked={prefs.autoDeal}
                   onChange={(v) => setPref("autoDeal", v)} />
            <Seg label="Deck"
                 options={[{ id: "red", label: "Red" }, { id: "plum", label: "Plum" }]}
                 value={prefs.deck}
                 onPick={(id) => setPref("deck", id as DeckColour)} />
            <Check label="Less motion"
                   hint={osReduced
                     ? "Your device already asks for reduced motion, so this is on whatever you choose here."
                     : "Cards and chips fade instead of travelling."}
                   checked={prefs.reducedMotion || osReduced}
                   disabled={osReduced}
                   onChange={(v) => setPref("reducedMotion", v)} />
          </Group>

          <Group name="Sound and table talk">
            {/*
              * ================ PHASE 6: BOTH OF THESE ARE REAL NOW ================
              * They were stored and nothing else since Phase 2b, and the hints said so
              * in as many words, because a switch that claims to mute something silent
              * is the same class of lie as a wallet balance nobody has.
              *
              * The sprite exists (`table/sounds.ts`, `public/sound/`) and the triggers
              * exist (`table/voice.ts`), so the hints stop apologising and start
              * describing. The volume slider is new and belongs beside the switch: a
              * player at a table at midnight wants it quieter, not off.
              * ====================================================================
              */}
            <Check label="Sounds"
                   hint="Cards, chips, the pot and the hurry-up clock. Starts on your first tap — browsers will not let a page make a noise before that."
                   checked={prefs.sound}
                   onChange={(v) => { setPref("sound", v); setMuted(!v); if (v) unlock(); }} />
            <Num label="Volume" hint="Quiet enough for a table at midnight."
                 value={Math.round(prefs.soundVolume * 10)} min={0} max={10}
                 onChange={(v) => { setPref("soundVolume", v / 10); setVolume(v / 10); }} />
            <Check label="Table talk"
                   hint="The cast's remarks, their faces, and your own taunt tray. Off hides every bubble and puts the tray away."
                   checked={prefs.taunts} onChange={(v) => setPref("taunts", v)} />
          </Group>

          <Group name="You">
            <p className="sheet-line">
              <Link to="/basics/persona" onClick={onClose}>Choose your face</Link> — the
              portrait on your seat, and in the saloon.
            </p>
          </Group>

          <details className="sheet-adv">
            <summary>Advanced</summary>
            {/*
              * The old setup form's "More options", to the field.
              *
              * These four decide how a table is DEALT rather than what is dealt, which
              * is why they are settings and not dials: nobody chooses "seat 4" as a way
              * of choosing a game. Seed and seed lock especially — they belong to the
              * player who is checking the shuffle, not to the player who wants a hand.
              */}
            <Num label="Your seat" hint="Which chair you take when a table has that many."
                 value={prefs.heroSeat + 1} min={1} max={9}
                 onChange={(v) => setPref("heroSeat", v - 1)} />
            <Check label="Faster bots"
                   hint="Fewer simulations per decision. Use this on an older phone."
                   checked={prefs.fastBots} onChange={(v) => setPref("fastBots", v)} />
            <label className="sheet-field">
              <span>Seed</span>
              <input type="text" spellCheck={false} placeholder="random"
                     value={prefs.seedLocked ? prefs.seed : ""}
                     onChange={(e) => {
                       const v = e.target.value;
                       if (v.trim() === "") { setPref("seedLocked", false); setPref("seed", ""); }
                       else { setPref("seed", v); setPref("seedLocked", true); }
                     }} />
              <small className="muted">
                Empty is a fresh 256-bit shuffle every table. Put something in — a phrase,
                or a seed from a previous session — and you get that exact table back.{" "}
                <Link to="/fair" onClick={onClose}>How the shuffle works</Link>.
              </small>
            </label>
          </details>

          <p className="sheet-foot muted small">
            Kept on this device. Nothing here is sent anywhere, and none of it changes
            what the cards do.
          </p>
        </div>
      </aside>
    </div>
  ), document.body);
}

/* --------------------------------------------------------------- the parts --- */

function Group({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <section className="sheet-group">
      <h3>{name}</h3>
      {children}
    </section>
  );
}

function Seg({ label, options, value, onPick }: {
  label: string;
  options: { id: string; label: string; hint?: string }[];
  value: string;
  onPick: (id: string) => void;
}) {
  return (
    <div className="sheet-field">
      <span>{label}</span>
      <div className="sheet-seg" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button key={o.id} type="button" role="radio" aria-checked={value === o.id}
                  title={o.hint} className={value === o.id ? "on" : ""}
                  onClick={() => onPick(o.id)}>{o.label}</button>
        ))}
      </div>
    </div>
  );
}

function Check({ label, hint, checked, onChange, disabled }: {
  label: string; hint?: string; checked: boolean; disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="sheet-check">
      <input type="checkbox" checked={checked} disabled={disabled}
             onChange={(e) => onChange(e.target.checked)} />
      <span>
        {label}
        {hint && <small className="muted">{hint}</small>}
      </span>
    </label>
  );
}

function Num({ label, hint, value, min, max, onChange }: {
  label: string; hint?: string; value: number; min: number; max: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="sheet-field">
      <span>{label}</span>
      <input type="range" min={min} max={max} step={1} value={value}
             onChange={(e) => onChange(Number(e.target.value))} />
      <output className="num">{value}</output>
      {hint && <small className="muted">{hint}</small>}
    </label>
  );
}

/** The four Level 3 values a table needs when it is dealt. Read once, at sit-down. */
export function level3Setup(): { heroSeat: number; fastBots: boolean; seed: string; seedLocked: boolean } {
  const p = getPrefs();
  return { heroSeat: p.heroSeat, fastBots: p.fastBots, seed: p.seed, seedLocked: p.seedLocked };
}
